import React, {FC, useState} from 'react'
import {
    DPCalendar,
    DPDay,
    DPDayRange,
    DPMonth,
    DPOffsetValue,
    DPPropGetter,
    DPPropsGetterConfig,
    DPYear,
    useDatePicker
} from '@rehookify/datepicker'
import {capitalize, getIntlLocale} from '@utilities/helpers.ts'
import {cn} from '@utilities/helpers'
import {setDay} from 'date-fns'
import {formatLocaleDate} from '@/dates.ts'
import {ChevronLeftIcon, ChevronRightIcon} from '@components/ui/icon'
import {Button} from '@components/ui/button'
import {useTranslation} from '@/translations/i18n.tsx'

export type View = 'date' | 'month' | 'year'

const calendarWrapperClassName = 'flex w-80 flex-col items-end gap-1 p-6'

const calendarHeaderClassName =
    'flex w-full items-center justify-between [&_h3]:grow [&_h3]:text-center [&_h3]:text-lg [&_h3]:font-extrabold [&_h3]:text-neutral-900'

const dayGridClassName = 'grid grid-cols-[repeat(7,40px)] gap-y-1'

const yearMonthGridClassName = 'grid w-full grid-cols-3 gap-2'

const weekDayClassName = 'flex h-10 items-center justify-center text-sm font-bold text-black'

const textButtonClassName = 'cursor-pointer transition-colors hover:text-primary-600'

const getDayWrapperClassName = ($rangeStatus: DPDayRange, $inCurrentMonth: boolean) =>
    cn(
        'nth-[7n]:rounded-r-full [&.lastMonthDay]:rounded-r-full',
        'nth-[7n+1]:rounded-l-full [&.firstMonthDay]:rounded-l-full',
        $inCurrentMonth && $rangeStatus && 'bg-neutral-100 font-medium text-neutral-700',
        ($rangeStatus === 'range-end' || $rangeStatus === 'will-be-range-end') && 'rounded-[0_50%_50%_0]',
        ($rangeStatus === 'range-start' || $rangeStatus === 'will-be-range-start') && 'rounded-[50%_0_0_50%]',
        !$inCurrentMonth && 'invisible'
    )

const getDayButtonClassName = ($rangeStatus: DPDayRange, $isSelected: boolean) =>
    cn(
        'flex size-10 cursor-pointer items-center justify-center rounded-full',
        $isSelected && 'bg-primary-600 font-medium text-white transition-colors hover:bg-primary-700',
        ($rangeStatus === 'in-range' || $rangeStatus === 'will-be-in-range') && 'bg-transparent',
        !$isSelected && $rangeStatus === '' && 'text-neutral-700 hover:not-disabled:bg-neutral-50',
        'disabled:cursor-not-allowed disabled:text-neutral-500'
    )

type DateSelectorProps = {
    mode: 'single' | 'multiple' | 'range'
    calendar: DPCalendar
    addOffset?: (offsetValue: DPOffsetValue, config?: DPPropsGetterConfig) => DPPropGetter
    subtractOffset?: (offsetValue: DPOffsetValue, config?: DPPropsGetterConfig) => DPPropGetter
    dayButton: (day: DPDay) => DPPropGetter
    weekDays: string[]
    locale?: string
    setViewState: React.Dispatch<React.SetStateAction<View>>
    withLeadingZeros?: boolean
    selectedDatesComponent?: React.ReactNode
    isDateDisabled?: (date: Date) => boolean
}

const DateSelector: FC<DateSelectorProps> = ({
    calendar,
    addOffset,
    subtractOffset,
    weekDays,
    dayButton,
    setViewState,
    withLeadingZeros,
    selectedDatesComponent,
    isDateDisabled
}) => {
    const inMonthDays = calendar.days.filter(day => day.inCurrentMonth)

    return (
        <div className={calendarWrapperClassName}>
            <div className={calendarHeaderClassName}>
                <Button
                    className={subtractOffset ? '' : 'invisible'}
                    shape="square"
                    variant="ghost"
                    {...subtractOffset?.({months: 1})}
                >
                    <ChevronLeftIcon size={20} />
                </Button>
                <h3>
                    <button type="button" className={textButtonClassName} onClick={() => setViewState('month')}>
                        {capitalize(calendar.month)}
                    </button>{' '}
                    <button type="button" className={textButtonClassName} onClick={() => setViewState?.('year')}>
                        {calendar.year}
                    </button>
                </h3>
                <Button
                    className={addOffset ? '' : 'invisible'}
                    shape="square"
                    variant="ghost"
                    {...addOffset?.({months: 1})}
                >
                    <ChevronRightIcon size={20} />
                </Button>
            </div>
            {selectedDatesComponent}
            <div className={dayGridClassName}>
                {weekDays.map((_, index) => (
                    <div key={index} className={weekDayClassName}>
                        {capitalize(formatLocaleDate(setDay(new Date(), (index + 7) % 7), 'EEEE').slice(0, 2))}
                    </div>
                ))}
            </div>
            <div className={dayGridClassName}>
                {calendar.days.map((day, index) => {
                    const isFirstDayOfTheMonth = inMonthDays[0].$date.toISOString() == day.$date.toISOString()
                    const isLastDayOfTheMonth =
                        inMonthDays[inMonthDays.length - 1].$date.toISOString() == day.$date.toISOString()

                    return (
                        <div
                            key={`${calendar.year}-${calendar.month}-${day.day}-${index}`}
                            className={cn(
                                getDayWrapperClassName(day.range, day.inCurrentMonth),
                                isFirstDayOfTheMonth && 'firstMonthDay',
                                isLastDayOfTheMonth && 'lastMonthDay'
                            )}
                        >
                            <button
                                className={getDayButtonClassName(day.range, day.inCurrentMonth && day.selected)}
                                {...dayButton(day)}
                                disabled={!day.inCurrentMonth || day.disabled || !!isDateDisabled?.(day.$date)}
                            >
                                {withLeadingZeros ? String(day.$date.getDate()).padStart(2, '0') : day.$date.getDate()}
                            </button>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

type MonthSelectorProps = {
    months: DPMonth[]
    monthButton: (month: DPMonth) => DPPropGetter
    currentMonth: string
    setViewState: React.Dispatch<React.SetStateAction<View>>
}

const MonthSelector: FC<MonthSelectorProps> = ({months, monthButton, currentMonth, setViewState}) => {
    const {t} = useTranslation()

    return (
        <div className={calendarWrapperClassName}>
            <div className={calendarHeaderClassName}>
                <h3>{t('components:commons:calendar:months')}</h3>
            </div>
            <div className={yearMonthGridClassName}>
                {months.map(month => (
                    <Button
                        key={month.month}
                        variant={month.month == currentMonth ? 'primary' : 'ghost'}
                        {...monthButton(month)}
                        onClick={e => {
                            monthButton(month).onClick?.(e)
                            setViewState('date')
                        }}
                    >
                        {capitalize(formatLocaleDate(month.$date, 'MMM'))}
                    </Button>
                ))}
            </div>
            <Button variant="ghost" onClick={() => setViewState('date')}>
                {t('components:commons:calendar:back')}
            </Button>
        </div>
    )
}

type YearSelectorProps = {
    years: DPYear[]
    yearButton: (year: DPYear) => DPPropGetter
    nextYearsButton: () => DPPropGetter
    previousYearsButton: () => DPPropGetter
    currentYear: number
    setViewState: React.Dispatch<React.SetStateAction<View>>
}

const YearSelector: FC<YearSelectorProps> = ({
    years,
    yearButton,
    nextYearsButton,
    previousYearsButton,
    currentYear,
    setViewState
}) => {
    const {t} = useTranslation()

    return (
        <div className={calendarWrapperClassName}>
            <div className={calendarHeaderClassName}>
                <Button shape="square" variant="ghost" {...previousYearsButton()}>
                    <ChevronLeftIcon />
                </Button>
                <h3>{t('components:commons:calendar:years')}</h3>
                <Button shape="square" variant="ghost" {...nextYearsButton()}>
                    <ChevronRightIcon />
                </Button>
            </div>
            <div className={yearMonthGridClassName}>
                {years.map(year => (
                    <Button
                        key={year.year}
                        {...yearButton(year)}
                        variant={year.year == currentYear ? 'primary' : 'ghost'}
                        onClick={event => {
                            yearButton(year).onClick?.(event)
                            setViewState('date')
                        }}
                    >
                        {formatLocaleDate(year.$date, 'yyyy')}
                    </Button>
                ))}
            </div>
            <Button variant="ghost" onClick={() => setViewState('date')}>
                {t('components:commons:calendar:back')}
            </Button>
        </div>
    )
}

export type CalendarProps = {
    numMonths: number
    selectedDates: Date[]
    onDatesChange: (dates: Date[]) => void
    mode: 'single' | 'multiple' | 'range'
    toggle?: boolean
    minDate?: Date
    maxDate?: Date
    locale?: string
    className?: string
    withLeadingZeros?: boolean
    selectedDatesComponent?: React.ReactNode
    isDateDisabled?: (date: Date) => boolean
}

export const Calendar: FC<CalendarProps> = ({
    numMonths,
    selectedDates,
    onDatesChange,
    mode,
    toggle = false,
    minDate,
    maxDate,
    locale = 'en',
    className,
    selectedDatesComponent,
    isDateDisabled,
    withLeadingZeros = true
}) => {
    const {
        data: {weekDays, calendars, months, years},
        propGetters: {
            dayButton,
            addOffset,
            subtractOffset,
            monthButton,
            yearButton,
            nextYearsButton,
            previousYearsButton
        }
    } = useDatePicker({
        locale: {locale: getIntlLocale()},
        selectedDates,
        onDatesChange,
        dates: {toggle, mode, minDate, maxDate},
        calendar: {
            startDay: 0,
            offsets: [...Array(numMonths).keys()].slice(1)
        }
    })

    const [view, setView] = useState<View>('date')

    return (
        <div className={cn('flex', className)}>
            {view == 'year' ? (
                <YearSelector
                    setViewState={setView}
                    years={years}
                    yearButton={yearButton}
                    nextYearsButton={nextYearsButton}
                    previousYearsButton={previousYearsButton}
                    currentYear={calendars[0].days[0].$date.getFullYear()}
                />
            ) : view == 'month' ? (
                <MonthSelector
                    setViewState={setView}
                    months={months}
                    monthButton={monthButton}
                    currentMonth={calendars[0].month}
                />
            ) : (
                calendars.map((calendar, index) => (
                    <DateSelector
                        mode={mode}
                        setViewState={setView}
                        locale={locale}
                        key={`${calendar.month}-${calendar.year}`}
                        calendar={calendar}
                        dayButton={dayButton}
                        weekDays={weekDays}
                        subtractOffset={index == 0 ? subtractOffset : undefined}
                        addOffset={index == calendars.length - 1 ? addOffset : undefined}
                        withLeadingZeros={withLeadingZeros}
                        selectedDatesComponent={selectedDatesComponent}
                        isDateDisabled={isDateDisabled}
                    />
                ))
            )}
        </div>
    )
}
